import { memo, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import { Check, Copy } from 'lucide-react';
import 'katex/dist/katex.min.css';

function textOf(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (node && typeof node === 'object' && 'props' in (node as any)) return textOf((node as any).props.children);
  return '';
}

function CodeBlock({ children, className }: { children: ReactNode; className?: string }) {
  const [copied, setCopied] = useState(false);
  const lang = /language-([\w-]+)/.exec(className ?? '')?.[1];
  return (
    <div className="code-block">
      <div className="code-block__bar">
        <span>{lang ?? 'code'}</span>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          aria-label="Copy code"
          onClick={() => { void navigator.clipboard.writeText(textOf(children)); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <pre><code className={className}>{children}</code></pre>
    </div>
  );
}

/** Renders model or user Markdown safely: raw HTML is not interpreted (react-markdown escapes it). */
export const Markdown = memo(function Markdown({ text }: { text: string }) {
  return (
    <div className="prose">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: true }]]}
        rehypePlugins={[rehypeKatex, [rehypeHighlight, { detect: true, ignoreMissing: true }]]}
        components={{
          a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer nofollow">{children}</a>,
          pre: ({ children }) => <>{children}</>,
          code: ({ className, children, ...rest }) => {
            const block = /language-/.test(className ?? '') || String(children).includes('\n');
            return block ? <CodeBlock className={className}>{children}</CodeBlock> : <code className="inline-code" {...rest}>{children}</code>;
          },
          table: ({ children }) => <div className="table-wrap"><table>{children}</table></div>,
          img: ({ src, alt }) => <img src={typeof src === 'string' ? src : undefined} alt={alt ?? ''} loading="lazy" referrerPolicy="no-referrer" />,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});
